import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Keyboard } from 'react-native';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import {
  selectMergedCustomers,
  setCloudCustomerSummaries,
  upsertCloudCustomerSummary,
} from '../../redux/OrdersSlice';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { usePhoneContacts } from '../../hooks/usePhoneContacts';
import { useCreateOrderCustomer } from '../../hooks/useCreateOrderCustomer';
import { useDetectedCountry } from '../../hooks/useDetectedCountry';
import { getCustomerOrderSummaries } from '../../api/orders';
import { AppSnackbar } from '../../services/AppSnackbar';
import { DEFAULT_CALLING_CODE } from '../../CONSTANTS';
import { WALK_IN_CUSTOMER } from '../../utils/saleCustomer';
import {
  buildPickerRows,
  contactsNotInCustomers,
  customerKey,
  describeChoice,
  filterContacts,
  filterCustomers,
  hasExactCustomerMatch,
  pluralize,
  toE164Phone,
  type ContactRowState,
  type ContactsListStatus,
  type OrderCustomerChoice,
  type PickerContact,
  type PickerCustomer,
} from '../../utils/customerPicker';
import CustomerStaffPicker, {
  type LockedCustomerView,
  type NewCustomerSubmitResult,
  type PickerPage,
} from './CustomerStaffPicker';
import type { NewCustomerFormData } from './NewCustomerPage';
import type { StaffAssignModel } from './StaffAssignSection';
import type { Customer } from '../../types/customers';

export interface PickerFooterState {
  /** The contact being added, if any. */
  busyName: string | null;
  /** Who the order is for. */
  choice: OrderCustomerChoice;
}

export interface OrderCustomerPickerProps {
  variant: 'sheet' | 'embedded';
  /** Loads customers and contacts only while true (the sheet is open, the tab shown). */
  active: boolean;
  title?: string;
  subtitle?: string;
  onClose?: () => void;
  /** The order's customer as held by the caller (name + back-office id). */
  selectedName: string;
  selectedCustomerId: string | null;
  onSelect: (name: string, customerId: string | null) => void;
  /** A customer the order keeps: the list is replaced by a locked card. */
  lockedCustomer?: { name: string } | null;
  staff: StaffAssignModel | null;
  /** The sheet's main button. */
  renderFooter?: (state: PickerFooterState) => React.ReactNode;
  /** Rows shown before a "search to find more" note, for an embedded list. */
  limits?: { customers: number; contacts: number };
  onPageChange?: (page: PickerPage) => void;
  /** Shown instead of saving a customer (demo mode, which has no account). */
  addCustomerUnavailableMessage?: string;
}

type CreatedCustomers = Record<string, { phone?: string; matched?: boolean }>;

/**
 * The customer picker wired up: customers from the store (fetched once when
 * empty), contacts from the phone, and a tap on a contact or the New customer
 * form saving a customer and putting them on the order.
 */
const OrderCustomerPicker: React.FC<OrderCustomerPickerProps> = ({
  variant,
  active,
  title,
  subtitle,
  onClose,
  selectedName,
  selectedCustomerId,
  onSelect,
  lockedCustomer,
  staff,
  renderFooter,
  limits,
  onPageChange,
  addCustomerUnavailableMessage,
}) => {
  const dispatch = useAppDispatch();
  const axiosprivate = useAxiosPrivate();
  const { auth } = useAuth();
  const customers = useAppSelector(selectMergedCustomers) as PickerCustomer[];
  const cloudCustomerCount = useAppSelector(
    state => state.orders.cloudCustomerSummaries.length,
  );
  const { createOrderCustomer, locationId } = useCreateOrderCustomer();
  const { callingCode, countryCode, iCountry } = useDetectedCountry();
  const phoneContacts = usePhoneContacts(active && !lockedCustomer);

  const [query, setQuery] = useState('');
  const [customersError, setCustomersError] = useState(false);
  const [customersAttempt, setCustomersAttempt] = useState(0);
  const [contactRowState, setContactRowState] = useState<
    Record<string, ContactRowState>
  >({});
  const [createdCustomers, setCreatedCustomers] = useState<CreatedCustomers>(
    {},
  );
  // Contact id → the customer it was saved as (null while saving), so the
  // tapped row keeps its place from the tap on.
  const [tappedContacts, setTappedContacts] = useState<
    Record<string, string | null>
  >({});
  const [busyContact, setBusyContact] = useState<PickerContact | null>(null);
  const [savingNames, setSavingNames] = useState<Record<string, string>>({});
  // Order names saved from here → the count they were listed with, which
  // keeps their place in the list.
  const [heldCounts, setHeldCounts] = useState<Record<string, number>>({});
  const [creating, setCreating] = useState(false);
  const savingRef = useRef<Set<string>>(new Set());
  const savingNamesRef = useRef<Set<string>>(new Set());
  // The order's customer when a save finishes, after any taps made meanwhile.
  const selectionRef = useRef({
    name: selectedName,
    customerId: selectedCustomerId,
  });
  useEffect(() => {
    selectionRef.current = {
      name: selectedName,
      customerId: selectedCustomerId,
    };
  }, [selectedName, selectedCustomerId]);

  // The store holds customers once the home screen has loaded them; a sheet
  // opened before that (or after a failed load) fetches them itself.
  useEffect(() => {
    if (
      !active ||
      cloudCustomerCount > 0 ||
      !locationId ||
      !auth?.accessToken
    ) {
      return;
    }
    let cancelled = false;
    getCustomerOrderSummaries(
      axiosprivate as any,
      locationId,
      { accessToken: auth.accessToken },
      1,
      200,
      { throwOnError: true },
    )
      .then(result => {
        if (cancelled) return;
        setCustomersError(false);
        if (result.customers.length > 0) {
          dispatch(setCloudCustomerSummaries(result.customers));
        }
      })
      .catch(() => {
        if (!cancelled) setCustomersError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [
    active,
    cloudCustomerCount,
    locationId,
    auth?.accessToken,
    axiosprivate,
    dispatch,
    customersAttempt,
  ]);

  const contactsStatus: ContactsListStatus = useMemo(() => {
    switch (phoneContacts.status) {
      case 'unavailable':
        return 'hidden';
      case 'checking':
        return 'loading';
      default:
        return phoneContacts.status;
    }
  }, [phoneContacts.status]);

  const choice = useMemo(
    () => describeChoice(selectedName, selectedCustomerId, customers),
    [selectedName, selectedCustomerId, customers],
  );

  const rows = useMemo(
    () =>
      buildPickerRows({
        query,
        choice,
        customers,
        customersError,
        contacts: phoneContacts.contacts,
        contactsStatus,
        contactRowState,
        createdCustomers,
        savingNames,
        tappedContacts,
        heldCounts,
        limits,
      }),
    [
      query,
      choice,
      customers,
      customersError,
      phoneContacts.contacts,
      contactsStatus,
      contactRowState,
      createdCustomers,
      savingNames,
      tappedContacts,
      heldCounts,
      limits,
    ],
  );

  // The search stays, so the tapped row stays where it is, ticked.
  const select = useCallback(
    (name: string, customerId: string | null) => {
      onSelect(name, customerId);
      Keyboard.dismiss();
    },
    [onSelect],
  );

  // A name no one has goes on the order and shows under This order at the
  // top of the full list.
  const chooseOrderName = useCallback(
    (name: string) => {
      select(name, null);
      setQuery('');
    },
    [select],
  );

  const setRowState = useCallback(
    (contactId: string, state: ContactRowState | null) => {
      setContactRowState(prev => {
        const next = { ...prev };
        if (state) next[contactId] = state;
        else delete next[contactId];
        return next;
      });
    },
    [],
  );

  /** True, after saying so, when customers can't be saved from here. */
  const addCustomerUnavailable = useCallback(() => {
    if (!addCustomerUnavailableMessage) return false;
    AppSnackbar.show(addCustomerUnavailableMessage, 'info', {
      durationMs: 5000,
    });
    return true;
  }, [addCustomerUnavailableMessage]);

  /** Saves the contact as a customer, or finds the customer who has that phone, and puts them on the order. */
  const saveContact = useCallback(
    async (contact: PickerContact) => {
      if (addCustomerUnavailable()) return;
      if (savingRef.current.has(contact.id)) return;
      savingRef.current.add(contact.id);
      setRowState(contact.id, 'saving');
      setBusyContact(contact);
      // Before the save: the store gets the new customer before it returns.
      setTappedContacts(prev => ({ ...prev, [contact.id]: null }));
      try {
        const { customer, existed } = await createOrderCustomer({
          name: contact.name,
          phone: toE164Phone(
            contact.phone,
            callingCode || DEFAULT_CALLING_CODE,
          ),
        });
        setRowState(contact.id, null);
        setCreatedCustomers(prev => ({
          ...prev,
          [customer._id]: { phone: contact.phone, matched: existed },
        }));
        setTappedContacts(prev => ({ ...prev, [contact.id]: customer._id }));
        select(customer.bp_customer_name, customer._id);
        if (existed) {
          AppSnackbar.show(
            `${customer.bp_customer_name} is already a customer · selected the existing record`,
            'info',
          );
        }
      } catch (error: any) {
        setRowState(contact.id, 'error');
        setTappedContacts(prev => {
          const next = { ...prev };
          delete next[contact.id];
          return next;
        });
        AppSnackbar.show(
          String(error?.message || 'Failed to add customer'),
          'error',
        );
      } finally {
        savingRef.current.delete(contact.id);
        setBusyContact(current =>
          current?.id === contact.id ? null : current,
        );
      }
    },
    [
      addCustomerUnavailable,
      callingCode,
      createOrderCustomer,
      select,
      setRowState,
    ],
  );

  /**
   * Saves a name known only from past orders as a customer and puts it on
   * the order: by name at once, linked to the new record once saved unless
   * another customer was picked meanwhile.
   */
  const saveOrderName = useCallback(
    async (listed: PickerCustomer) => {
      if (addCustomerUnavailable()) return;
      const key = customerKey(listed.name);
      if (savingNamesRef.current.has(key)) return;
      savingNamesRef.current.add(key);
      setSavingNames(prev => ({ ...prev, [key]: listed.name }));
      setHeldCounts(prev =>
        Object.prototype.hasOwnProperty.call(prev, key)
          ? prev
          : { ...prev, [key]: listed.count },
      );
      select(listed.name, listed.customerId);
      try {
        const { customer, ordersLinked } = await createOrderCustomer({
          name: listed.name,
        });
        const current = selectionRef.current;
        if (!current.customerId && customerKey(current.name) === key) {
          onSelect(customer.bp_customer_name, customer._id);
        }
        // The row keeps its place; its count can change, so say why.
        if (ordersLinked > 0) {
          AppSnackbar.show(
            `${customer.bp_customer_name} saved · ${pluralize(
              ordersLinked,
              'past order',
            )} linked`,
            'success',
          );
        }
      } catch (error: any) {
        AppSnackbar.show(
          String(error?.message || 'Failed to add customer'),
          'error',
        );
      } finally {
        savingNamesRef.current.delete(key);
        setSavingNames(prev => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    [addCustomerUnavailable, createOrderCustomer, onSelect, select],
  );

  const submitNewCustomer = useCallback(
    async (data: NewCustomerFormData): Promise<NewCustomerSubmitResult> => {
      if (addCustomerUnavailable()) return { saved: false };
      setCreating(true);
      try {
        const { customer, existed } = await createOrderCustomer({
          name: data.name,
          phone: data.phone,
          email: data.email,
          ...(data.allowDuplicateName ? { allowDuplicateName: true } : {}),
        });
        setCreatedCustomers(prev => ({
          ...prev,
          [customer._id]: { matched: existed },
        }));
        select(customer.bp_customer_name, customer._id);
        AppSnackbar.show(
          existed
            ? `${customer.bp_customer_name} is already a customer · selected the existing record`
            : `${customer.bp_customer_name} saved to customers`,
          existed ? 'info' : 'success',
        );
        return { saved: true };
      } catch (error: any) {
        // The name is already saved: the form lists those customers instead.
        if (error?.sameNameCustomers) {
          return { saved: false, sameName: error.sameNameCustomers };
        }
        AppSnackbar.show(
          String(error?.message || 'Failed to add customer'),
          'error',
        );
        return { saved: false };
      } finally {
        setCreating(false);
      }
    },
    [addCustomerUnavailable, createOrderCustomer, select],
  );

  /** A customer the New customer form listed for the name, put on the order. */
  const pickExistingCustomer = useCallback(
    (customer: Customer) => {
      dispatch(
        upsertCloudCustomerSummary({
          _id: customer._id,
          bp_customer_name: customer.bp_customer_name,
          bp_order_count: 0,
        }),
      );
      select(customer.bp_customer_name, customer._id);
    },
    [dispatch, select],
  );

  // Done on the keyboard: the customer the text can only mean (an exact
  // name, or the one customer matching); with nothing listed the text
  // becomes the order's name; with several matches the list is left to tap.
  const submitQuery = useCallback(() => {
    const typed = query.trim();
    if (!typed) {
      Keyboard.dismiss();
      return;
    }
    const exact = hasExactCustomerMatch(customers, typed);
    if (exact) {
      select(exact.name, exact.customerId);
      return;
    }
    const customerMatches = filterCustomers(customers, typed);
    const contactMatches =
      contactsStatus === 'ready'
        ? filterContacts(
            contactsNotInCustomers(phoneContacts.contacts, customers),
            typed,
          )
        : [];
    if (customerMatches.length === 1 && contactMatches.length === 0) {
      select(customerMatches[0].name, customerMatches[0].customerId);
    } else if (customerMatches.length === 0 && contactMatches.length === 0) {
      chooseOrderName(typed);
    } else {
      Keyboard.dismiss();
    }
  }, [
    chooseOrderName,
    contactsStatus,
    customers,
    phoneContacts.contacts,
    query,
    select,
  ]);

  const lockedView: LockedCustomerView | null = useMemo(() => {
    if (!lockedCustomer) return null;
    const key = customerKey(lockedCustomer.name);
    const listed = customers.find(c => customerKey(c.name) === key);
    const firstName =
      lockedCustomer.name.trim().split(/\s+/)[0] || lockedCustomer.name;
    return {
      name: lockedCustomer.name,
      meta:
        listed && listed.count > 0
          ? pluralize(listed.count, 'order')
          : 'On this order',
      note: `This order already has a named customer, so it stays with ${firstName}.`,
    };
  }, [customers, lockedCustomer]);

  return (
    <CustomerStaffPicker
      variant={variant}
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      rows={rows}
      query={query}
      onQueryChange={setQuery}
      onSubmitQuery={submitQuery}
      lockedCustomer={lockedView}
      staff={staff}
      newCustomer={{
        countryCode,
        country: iCountry,
        submitting: creating,
        onSubmit: submitNewCustomer,
        onPickExisting: pickExistingCustomer,
      }}
      footer={renderFooter?.({
        busyName: busyContact?.name ?? Object.values(savingNames)[0] ?? null,
        choice,
      })}
      onPageChange={onPageChange}
      onSelectWalkIn={() => select(WALK_IN_CUSTOMER, null)}
      onSelectLabel={name => select(name, null)}
      onSelectCustomer={customer => select(customer.name, customer.customerId)}
      onSaveCustomer={saveOrderName}
      onSelectContact={saveContact}
      onRetryContact={saveContact}
      onUseLabel={chooseOrderName}
      onAllowContacts={() => void phoneContacts.request()}
      onOpenSettings={phoneContacts.openSettings}
      onRetryContacts={phoneContacts.reload}
      onRetryCustomers={() => {
        setCustomersError(false);
        setCustomersAttempt(n => n + 1);
      }}
    />
  );
};

export default OrderCustomerPicker;
